/**
 * Énumération des rôles utilisateurs (mirroir de
 * {@code com.ydrus.lacompassion.enums.RoleName} côté back).
 */
export const roles = {
  ROLE_ADMIN: "ROLE_ADMIN",
  ROLE_DOCTOR: "ROLE_DOCTOR",
  ROLE_INSURER: "ROLE_INSURER",
  ROLE_PATIENT: "ROLE_PATIENT",
  ROLE_SECRETARY: "ROLE_SECRETARY"
};

/**
 * Mapping rôle → segment d'URL pour la création d'un utilisateur.
 * <p>
 * Cf. {@code UserController} : seuls trois endpoints de création existent
 *   - {@code POST /users/staff}    pour ROLE_ADMIN, ROLE_DOCTOR, ROLE_SECRETARY
 *   - {@code POST /users/patient}  pour ROLE_PATIENT
 *   - {@code POST /users/insurer}  pour ROLE_INSURER
 * <p>
 * Auparavant le mapping pointait sur {@code "admin"} pour les rôles staff,
 * ce qui produisait un 404 muet depuis le renommage de l'endpoint
 * {@code /users/admin} → {@code /users/staff} (Phase 1).
 */
export const rolesPath = {
  ROLE_ADMIN: "staff",
  ROLE_DOCTOR: "staff",
  ROLE_SECRETARY: "staff",
  ROLE_INSURER: "insurer",
  ROLE_PATIENT: "patient"
};

/**
 * Rôles du personnel, seuls rôles cumulables (cf. règle back : un rôle
 * additionnel ne peut être qu'un rôle staff, et seulement sur un compte
 * dont le rôle principal est lui-même un rôle staff). L'ordre est celui
 * d'affichage dans les écrans d'administration.
 */
export const STAFF_ROLES = [
  roles.ROLE_DOCTOR,
  roles.ROLE_SECRETARY,
  roles.ROLE_ADMIN
];

/**
 * Libellés français des rôles — source de vérité unique pour les selects,
 * les pastilles ({@code RoleBadge}) et l'en-tête du tableau de bord.
 */
export const ROLE_LABELS = {
  [roles.ROLE_ADMIN]: "Administrateur",
  [roles.ROLE_DOCTOR]: "Médecin",
  [roles.ROLE_SECRETARY]: "Secrétaire",
  [roles.ROLE_PATIENT]: "Patient",
  [roles.ROLE_INSURER]: "Assureur"
};

/**
 * Normalise l'objet utilisateur reçu : le nouveau shape porte le
 * discriminant {@code role} à plat, l'ancien l'imbrique sous {@code .user}
 * (cf. {@code storageUtils.setUserData}). Centralisé ici pour que le repli
 * ne soit pas répété à chaque appelant.
 */
const resolveUser = (user) => (user?.role ? user : user?.user ?? null);

/**
 * Rôle principal de l'utilisateur (discriminant {@code role} de
 * {@code UserResponse}), ou {@code null}.
 */
export const getPrincipalRole = (user) => resolveUser(user)?.role ?? null;

/**
 * Rôles additionnels de l'utilisateur, hors rôle principal. Tolère
 * l'absence du champ (comptes créés avant la fonctionnalité, ou back
 * antérieur) en renvoyant un tableau vide.
 */
export const getAdditionalRoles = (user) => {
  const resolvedUser = resolveUser(user);
  const additionalRoles = resolvedUser?.additionalRoles;
  if (!Array.isArray(additionalRoles)) return [];
  return additionalRoles.filter((role) => role && role !== resolvedUser?.role);
};

/**
 * Autorités effectives de l'utilisateur : {@code {role} ∪ additionalRoles}
 * (miroir exact de la règle back). C'est la seule base de décision pour
 * les droits — jamais le seul rôle principal.
 *
 * @example
 *   getEffectiveRoles({ role: "ROLE_ADMIN", additionalRoles: ["ROLE_DOCTOR"] })
 *   // → ["ROLE_ADMIN", "ROLE_DOCTOR"]
 */
export const getEffectiveRoles = (user) => {
  const principalRole = getPrincipalRole(user);
  const additionalRoles = getAdditionalRoles(user);
  return principalRole ? [principalRole, ...additionalRoles] : additionalRoles;
};

/** Indique si {@code user} détient {@code role} parmi ses autorités effectives. */
export const hasRole = (user, role) => getEffectiveRoles(user).includes(role);

/** Indique si {@code user} détient au moins un des {@code rolesToCheck}. */
export const hasAnyRole = (user, rolesToCheck = []) =>
  rolesToCheck.some((role) => hasRole(user, role));

/**
 * Helpers polymorphiques pour discriminer un {@code UserResponse}. Ils
 * remplacent les comparaisons manuelles {@code user.roleName === "ROLE_X"}
 * qui mélangeaient le champ legacy {@code roleName} (DTO request) avec le
 * discriminant de réponse {@code role}.
 * <p>
 * Tous s'appuient sur les autorités effectives : un médecin qui porte
 * {@code ROLE_ADMIN} en rôle additionnel est administrateur au sens des
 * droits. {@code isPatient} / {@code isInsurer} passent par le même helper
 * par cohérence, mais ces deux rôles ne sont jamais cumulés (règle back) :
 * ils restent donc de fait des tests sur le rôle principal.
 *
 * @example
 *   if (isPatient(user)) { ... }
 *   if (isStaff(user))   { ... }
 */
export const isAdmin     = (user) => hasRole(user, roles.ROLE_ADMIN);
export const isDoctor    = (user) => hasRole(user, roles.ROLE_DOCTOR);
export const isSecretary = (user) => hasRole(user, roles.ROLE_SECRETARY);
export const isPatient   = (user) => hasRole(user, roles.ROLE_PATIENT);
export const isInsurer   = (user) => hasRole(user, roles.ROLE_INSURER);
export const isStaff     = (user) => hasAnyRole(user, STAFF_ROLES);

/**
 * Chemins des écrans protégés, miroir des {@code path} déclarés dans
 * {@code Routes.js}. Centralisés ici pour que la liste blanche de droits
 * ci-dessous et le menu latéral (<DashboardWrapper>) désignent exactement
 * les mêmes écrans.
 */
export const routePaths = {
  PATIENTS: "/patient",
  QUEUE_DOCTOR: "/file-attente/medecin",
  QUEUE_SECRETARY: "/file-attente/secretaire",
  APPOINTMENTS: "/rendez-vous",
  ACTS: "/examen",
  PHARMACY: "/pharmacy",
  CONSULTATION: "/consultation",
  PROFILE: "/profile",
  INSURERS: "/assureur",
  INSURER_DETAILS: "/assureur-details",
  DOCTORS: "/personnel-medecin",
  USERS: "/users",
  REPORTS: "/bilans"
};

/**
 * Écrans communs à tous les profils du personnel soignant/administratif.
 * La file d'attente en est exclue : chaque rôle a la sienne.
 */
const COMMON_STAFF_ROUTES = [
  routePaths.PATIENTS,
  routePaths.APPOINTMENTS,
  routePaths.ACTS,
  routePaths.PHARMACY,
  routePaths.CONSULTATION,
  routePaths.PROFILE
];

const DOCTOR_ROUTES = [...COMMON_STAFF_ROUTES, routePaths.QUEUE_DOCTOR];

// La secrétaire a les mêmes écrans que le médecin, plus la gestion des
// assureurs/entreprises, et sa propre file d'attente.
const SECRETARY_ROUTES = [
  ...COMMON_STAFF_ROUTES,
  routePaths.QUEUE_SECRETARY,
  routePaths.INSURERS,
  routePaths.INSURER_DETAILS
];

const ADMIN_ROUTES = Object.values(routePaths);

/**
 * Liste blanche des écrans autorisés par rôle du personnel — source de
 * vérité unique partagée par les gardes de route ({@code RoleProtected})
 * et par le menu latéral.
 * <p>
 * Règle intérimaire arrêtée par le métier en attendant un vrai système de
 * permissions : administrateur = tout ; médecin = son périmètre de soin ;
 * secrétaire = celui du médecin plus les assureurs.
 * <p>
 * Les rôles hors personnel ({@code ROLE_PATIENT}, {@code ROLE_INSURER})
 * n'y figurent volontairement pas : leur périmètre reste inchangé (simple
 * contrôle du token), cf. {@link canAccessRoute}.
 */
export const STAFF_ROUTES_BY_ROLE = {
  [roles.ROLE_ADMIN]: ADMIN_ROUTES,
  [roles.ROLE_DOCTOR]: DOCTOR_ROUTES,
  [roles.ROLE_SECRETARY]: SECRETARY_ROUTES
};

/**
 * Indique si {@code user} a le droit d'ouvrir l'écran {@code path}
 * (comparaison sur le chemin exact, cf. {@link routePaths}).
 * <p>
 * Le périmètre est l'<em>union</em> des listes blanches de toutes les
 * autorités effectives : une secrétaire qui porte {@code ROLE_DOCTOR} en
 * rôle additionnel cumule les deux périmètres.
 *
 * @example
 *   canAccessRoute(user, routePaths.USERS)
 */
export const canAccessRoute = (user, path) => {
  if (isAdmin(user)) return true;

  const allowedRoutesByRole = getEffectiveRoles(user)
    .map((role) => STAFF_ROUTES_BY_ROLE[role])
    .filter(Boolean);

  // Rôle hors personnel (patient, assureur) ou utilisateur inconnu :
  // comportement historique conservé, seul le token fait foi.
  if (allowedRoutesByRole.length === 0) return true;

  return allowedRoutesByRole.some((allowedRoutes) => allowedRoutes.includes(path));
};
